import React, { useState } from 'react';
import { linkifyText, Poppins, StyledAvatar } from 'src/common';
import { ThreeDots } from 'src/components/CommonMenu/comps';
import CommonMenu from 'src/components/CommonMenu';
import Button from 'src/components/form/Button';
import MaterialInput from 'src/components/form/MaterialInput';
import { ConfirmDeleteModal } from 'src/components/modals';
import { Event, EventKind, ObjectKind, useDeleteEventMutation, useUpsertEventMutation } from 'src/graphql/_generated';
import { useAuth } from 'src/state/auth';
import { getProfileCredentials } from 'src/utils/auth';
import { formatDate } from 'src/utils/misc';
import styled from 'styled-components';

const Div = styled.div`
  padding: 20px 22px;
  .cmm {
    display: flex;
    gap: 10px;
    &__content {
      display: flex;
      flex-direction: column;
      gap: 10px;
      flex: 1;
      min-width: 0;

      &__head {
        height: 34px;
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;

        &__name {
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        &__meta {
          display: flex;
          gap: 16px;
          align-items: center;
          flex-shrink: 0;
          svg {
            margin-bottom: 2px;
          }
        }
      }
      &__body {
        white-space: pre-wrap;
      }
      &__edit-actions {
        display: flex;
        gap: 8px;
      }
    }
  }
`;

interface CommentProps {
  event: Event;
  eventData: { objectType: ObjectKind; objectId: string };
  onMutate: () => void;
}

export const Comment: React.FC<CommentProps> = ({ event, eventData, onMutate }) => {
  const authorName = event.meta?.createdBy || 'Unknown';
  const { profile, workspaceId } = useAuth();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editText, setEditText] = useState(event.notes || '');

  const isOwnComment = profile?.user?.name === authorName;

  const [deleteEvent] = useDeleteEventMutation({
    onCompleted: onMutate,
  });

  const [upsertEvent, { loading: saving }] = useUpsertEventMutation({
    onCompleted: () => {
      setIsEditing(false);
      onMutate();
    },
  });

  const handleClose = () => setAnchorEl(null);

  const onMenuClick = (e: React.MouseEvent<HTMLLIElement>, cb: () => void) => {
    e.stopPropagation();
    handleClose();
    cb();
  };

  const handleDelete = () => {
    setIsDeleteModalOpen(true);
  };

  const handleEdit = () => {
    setEditText(event.notes || '');
    setIsEditing(true);
  };

  const handleSave = () => {
    const trimmed = editText.trim();
    if (!trimmed) return;

    upsertEvent({
      variables: {
        workspaceId,
        eventData: [
          {
            id: event.id,
            eventType: EventKind.Comment,
            notes: trimmed,
            ...eventData,
          },
        ],
      },
    });
  };

  return (
    <>
      <Div className="comment">
        <div className="cmm">
          <StyledAvatar $small>{getProfileCredentials(authorName)}</StyledAvatar>
          <div className="cmm__content">
            <div className="cmm__content__head">
              <Poppins weight={500} px={14} className="cmm__content__head__name">
                {authorName}
              </Poppins>
              <div className="cmm__content__head__meta">
                <Poppins px={12} color="cflowerBlue">
                  {event.meta?.createdAt ? formatDate(new Date(event.meta.createdAt), { formatType: 'datetime' }) : ''}
                </Poppins>
                <ThreeDots
                  alwaysActiveColor
                  onClick={(e: any) => setAnchorEl(e.currentTarget)}
                  dataCy="comment-dots-menu"
                />
                <CommonMenu
                  configuredList={[
                    isOwnComment && {
                      type: 'EDIT' as const,
                      exe: (e) => onMenuClick(e, handleEdit),
                    },
                    {
                      type: 'DELETE' as const,
                      exe: (e) => onMenuClick(e, handleDelete),
                    },
                  ]}
                  anchorEl={anchorEl}
                  open={Boolean(anchorEl)}
                  onClose={handleClose}
                />
              </div>
            </div>

            {isEditing ? (
              <>
                <MaterialInput
                  multiline
                  minRows={1}
                  maxRows={5}
                  fullWidth
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  error={editText.length > 1000}
                  helperText={editText.length > 1000 ? 'Cannot exceed 1000 characters' : undefined}
                />
                <div className="cmm__content__edit-actions">
                  <Button
                    primary
                    $small
                    onClick={handleSave}
                    disabled={
                      !editText.trim() ||
                      editText.trim() === (event.notes || '').trim() ||
                      editText.length > 1000 ||
                      saving
                    }
                  >
                    POST
                  </Button>
                  <Button secondary $small onClick={() => setIsEditing(false)}>
                    DISCARD CHANGES
                  </Button>
                </div>
              </>
            ) : (
              <Poppins px={14} className="cmm__content__body">
                {linkifyText(event.notes || '')}
              </Poppins>
            )}
          </div>
        </div>
      </Div>
      {isDeleteModalOpen && (
        <ConfirmDeleteModal
          onConfirm={() => deleteEvent({ variables: { id: event.id } })}
          onClose={() => setIsDeleteModalOpen(false)}
        />
      )}
    </>
  );
};
